import * as React from 'react';
import {
  Alert,
  Box,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Rule } from '../types';
import { formatTime } from './common';

interface Props {
  rules: Rule[];
  onAdd: (text: string) => Promise<void>;
  onToggle: (rule: Rule, active: boolean) => Promise<void>;
}

// Standing instructions to the system, in plain English.
export default function RulesPanel({ rules, onAdd, onToggle }: Props) {
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await onAdd(text.trim());
      setText('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (rule: Rule, active: boolean) => {
    setError(null);
    try {
      await onToggle(rule, active);
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: 820 }}>
      <Typography variant="body2" color="text.secondary">
        Rules override the system’s own judgment. Each morning it checks its top
        topics against them and skips any a rule excludes (the run report says
        which rule and why), and every draft is written to follow them. Rules
        can be switched off but not deleted, so past decisions keep the exact
        rule they followed.
      </Typography>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          id="seo-new-rule"
          fullWidth
          multiline
          minRows={2}
          label="New rule"
          placeholder="e.g. Don’t write about mattresses until the new mattress launches."
          value={text}
          onChange={e => setText(e.target.value)}
        />
        <LoadingButton
          variant="contained"
          loading={busy}
          disabled={text.trim().length < 3}
          onClick={add}
          sx={{ flexShrink: 0 }}
        >
          Add rule
        </LoadingButton>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {!rules.length && (
        <Typography color="text.secondary">No rules yet.</Typography>
      )}
      {rules.map(rule => (
        <Box
          key={rule.id}
          sx={{
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            opacity: rule.active ? 1 : 0.6,
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="body2">{rule.text}</Typography>
              <Typography variant="caption" color="text.secondary">
                Added by {rule.createdBy || 'someone'} ·{' '}
                {formatTime(rule.createdAt)}
                {!rule.active &&
                  ` · switched off ${formatTime(rule.updatedAt)}`}
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ flexShrink: 0, mr: 0 }}
              control={
                <Switch
                  checked={rule.active}
                  onChange={e => toggle(rule, e.target.checked)}
                />
              }
              label={rule.active ? 'On' : 'Off'}
            />
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
